import React, { useState } from 'react';
import { Card, CardContent, Button } from '@roger/ui-components';
import {
  FileText,
  ClipboardList,
  Pill,
  Bandage,
  User as UserIcon,
  History,
  Activity,
  ChevronDown,
  ChevronUp,
  Copy,
  MessageSquare,
  UserCheck,
  Stethoscope,
} from 'lucide-react';
import CardTitleWithIcon from './CardTitleWithIcon';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { MedicalData } from '@/types/medicalData.types';

interface PatientInformationSectionProps {
  medicalData: MedicalData;
  onOpenModal: (dataType: string, data: unknown) => void;
  handleOpenPdf: (pdfKey: string) => void;
  bulletedTranscriptChunks?: Record<string, string>;
  showTranscription?: boolean;
}

interface InfoItem {
  key: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}

const PatientInformationSection: React.FC<PatientInformationSectionProps> = ({
  medicalData,
  onOpenModal,
  handleOpenPdf,
  bulletedTranscriptChunks,
  showTranscription = true,
}) => {
  const [showAllPdfs, setShowAllPdfs] = useState(false);
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const pdfPaths = medicalData?.pdf_paths;
  const pdfKeys = Array.isArray(pdfPaths)
    ? pdfPaths
    : pdfPaths?.intake_documents;
  // New (unconsidered) documents — fetched into WellSky after the chart was set
  // aside, not yet part of the coding. Legacy array-shaped pdf_paths carries none.
  const newDocKeys =
    !Array.isArray(pdfPaths) && pdfPaths?.new_unconsidered_documents
      ? pdfPaths.new_unconsidered_documents
      : [];
  const hasNewDocs = newDocKeys.length > 0;
  const visiblePdfs = showAllPdfs ? pdfKeys : pdfKeys?.slice(0, 4);
  const hasMorePdfs = pdfKeys && pdfKeys.length > 4;

  // Extract filename from S3 key
  const extractFilename = (s3Key: string): string => {
    const parts = s3Key.split('/');
    return parts[parts.length - 1];
  };

  // Copy filename to clipboard
  const handleCopyFilename = async (
    filename: string,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(filename);
      showSuccess('Copied!', `Filename "${filename}" copied to clipboard`);
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      showError('Copy Failed', 'Failed to copy filename to clipboard');
    }
  };

  const referralHistoricalItems: InfoItem[] = [
    {
      key: 'home_health_orders',
      title: 'Home Health Order',
      icon: ClipboardList,
      onClick: () =>
        onOpenModal?.('home_health_orders', medicalData?.home_health_orders),
    },
    {
      key: 'demographics',
      title: 'Demographics',
      icon: UserIcon,
      onClick: () => onOpenModal?.('demographics', medicalData?.demographics),
    },
    {
      key: 'history',
      title: 'Past Diagnoses',
      icon: History,
      onClick: () =>
        onOpenModal?.(
          'past_visit_diagnoses',
          medicalData?.past_visit_diagnoses
        ),
    },
    {
      key: 'face_to_face_visits',
      title: 'Face-to-Face Visits',
      icon: UserCheck,
      onClick: () =>
        onOpenModal?.('face_to_face_visits', medicalData?.face_to_face_visits),
    },
    {
      key: 'authorized_diagnoses',
      title: 'Authorized Diagnoses',
      icon: Stethoscope,
      onClick: () =>
        onOpenModal?.(
          'authorized_diagnoses',
          medicalData?.authorized_diagnoses
        ),
    },
  ];

  const currentVisitItems: InfoItem[] = [
    {
      key: 'narrative',
      title: 'Narrative',
      icon: Activity,
      onClick: () => onOpenModal?.('narrative', medicalData?.narrative),
    },
    {
      key: 'medications',
      title: 'Medications',
      icon: Pill,
      onClick: () => onOpenModal?.('medications', medicalData?.medications),
    },
    {
      key: 'wounds',
      title: 'Wound Care',
      icon: Bandage,
      onClick: () => onOpenModal?.('wounds', medicalData?.wounds),
    },

    ...(showTranscription
      ? [
          {
            key: 'visit_transcript',
            title: 'Visit Transcript',
            icon: MessageSquare,
            onClick: () =>
              onOpenModal?.('visit_transcript', bulletedTranscriptChunks),
          },
        ]
      : []),

    {
      key: 'medical_form',
      title: 'Full Visit Data',
      icon: ClipboardList,
      onClick: () => onOpenModal?.('medical_form', medicalData?.medical_form),
    },
  ];

  const renderInfoItems = (items: InfoItem[]) => {
    return (
      <div className='grid grid-cols-4 gap-2'>
        {items.map(item => {
          const IconComponent = item.icon;
          return (
            <div
              key={item.key}
              onClick={item.onClick}
              className={`group p-2.5 border border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:border-primary-400 bg-white hover:bg-primary-50 active:scale-95`}
            >
              <div className='flex items-center gap-2'>
                <div className='h-8 w-8 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-primary-200'>
                  <IconComponent className='w-3.5 h-3.5 text-primary-700 transition-transform group-hover:scale-110' />
                </div>
                <span className='text-xs font-semibold text-gray-700 truncate group-hover:text-primary-900 transition-colors'>
                  {item.title}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // A single interactive PDF tile (clickable to open, hover-lift, copy-filename).
  // `isNew` rings the tile and adds a corner NEW badge for newly-arrived docs.
  const renderPdfTile = (pdfKey: string, key: React.Key, isNew = false) => {
    const filename = extractFilename(pdfKey);

    return (
      <div
        key={key}
        onClick={() => handleOpenPdf?.(pdfKey)}
        className={`relative group p-2.5 border rounded-lg bg-white cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:bg-primary-50 active:scale-95 ${
          isNew
            ? 'ring-2 ring-primary-400 border-primary-300'
            : 'border-gray-200 hover:border-primary-400'
        }`}
        title={filename}
      >
        {isNew && (
          <span className='absolute -top-1.5 -right-1.5 z-10 rounded-full bg-primary-600 text-white text-[9px] font-bold px-1.5 py-0.5 leading-none shadow-sm'>
            NEW (not used for coding yet)
          </span>
        )}
        <div className='flex items-center gap-2'>
          <div className='h-8 w-8 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-primary-200'>
            <FileText className='w-3.5 h-3.5 text-primary-700 transition-transform group-hover:scale-110' />
          </div>
          <span className='text-xs font-semibold text-gray-700 truncate group-hover:text-primary-900 transition-colors line-clamp-1'>
            {filename}
          </span>
        </div>
        <button
          type='button'
          onClick={e => handleCopyFilename(filename, e)}
          className='absolute top-1.5 right-1.5 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-all hover:bg-white hover:shadow-sm z-10'
          aria-label='Copy file name'
        >
          <Copy className='w-3 h-3 text-gray-600 hover:text-gray-900' />
        </button>
      </div>
    );
  };

  const renderPdfBoxes = () => {
    if (pdfKeys?.length === 0 && !hasNewDocs) return null;

    return (
      <div className='grid grid-cols-4 gap-2'>
        {/* New documents surface first and are exempt from the +N more collapse */}
        {newDocKeys.map((pdfKey, index) =>
          renderPdfTile(pdfKey, `new-${index}`, true)
        )}

        {visiblePdfs?.map((pdfKey, index) =>
          renderPdfTile(pdfKey, `intake-${index}`)
        )}

        {hasMorePdfs && !showAllPdfs && (
          <div
            onClick={() => setShowAllPdfs(true)}
            className='p-2 border rounded-md bg-gray-50 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 flex items-center justify-center'
          >
            <div className='flex items-center gap-2'>
              <div className='p-1 rounded bg-gray-100 flex items-center justify-center flex-shrink-0'>
                <ChevronDown className='w-3 h-3 text-gray-600' />
              </div>
              <span className='text-xs font-medium text-gray-800'>
                +{pdfKeys.length - 4} more
              </span>
            </div>
          </div>
        )}

        {hasMorePdfs && showAllPdfs && (
          <div className='col-span-4 flex justify-center mt-2'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setShowAllPdfs(false)}
              className='text-xs'
            >
              <ChevronUp className='w-3 h-3 mr-1' />
              Show less
            </Button>
          </div>
        )}
      </div>
    );
  };

  if (!medicalData) {
    return null;
  }

  return (
    <Card className='mb-6 relative overflow-hidden'>
      {/* Subtle background accent */}
      <div className='absolute -top-12 -right-12 w-32 h-32 bg-blue-200/20 rounded-full blur-3xl' />

      <CardContent className='relative z-10'>
        <CardTitleWithIcon
          icon={UserIcon}
          title='Patient Information'
          subtitle='Clinical data and documentation access'
        />

        <div className='space-y-3'>
          {/* Referral & Historical Section */}
          <div>
            <div className='flex items-center gap-2 mb-2'>
              <span className='text-xs font-semibold text-primary uppercase tracking-wide'>
                Referral & Historical
              </span>
            </div>
            {renderInfoItems(referralHistoricalItems)}
          </div>

          {/* PDFs Section */}
          {((pdfKeys && pdfKeys.length > 0) || hasNewDocs) && (
            <div>
              <div className='flex items-center gap-2 mb-2'>
                <span className='text-xs font-semibold text-primary uppercase tracking-wide'>
                  Documents (PDFs)
                </span>
              </div>
              {renderPdfBoxes()}
            </div>
          )}

          {/* Current Visit Section */}
          <div>
            <div className='flex items-center gap-2 mb-2'>
              <span className='text-xs font-semibold text-primary uppercase tracking-wide'>
                Current Visit
              </span>
            </div>
            {renderInfoItems(currentVisitItems)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PatientInformationSection;
